import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { API } from '@constants'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { serverUrl } from '@shared/utils/MkEnv'
import { useSiteContext } from './MkSiteContext'
import type { MeResponse } from '../types/me'

interface MeContextType {
  me: MeResponse | null
  isLoading: boolean
  error: Error | null
  refreshMe: () => Promise<void>
}

const MeContext = createContext<MeContextType | undefined>(undefined)

export const MeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthorized, session } = useAuthentication()
  const { themeBrand } = useSiteContext()
  const authHeaders = useAuthHeaders()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const fetchIdRef = useRef(0)
  const autoFetchKeyRef = useRef<string | null>(null)
  const hasSession = !!session

  const fetchMe = useCallback(async () => {
    if (!isAuthorized || !hasSession || !themeBrand) return

    const fetchId = ++fetchIdRef.current
    const url = `${serverUrl}${API}/me?brand=${encodeURIComponent(themeBrand)}`

    setMe(null)
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      })

      if (!res.ok) {
        throw new Error(`Failed to fetch /me: ${res.status} ${res.statusText}`)
      }

      const data = (await res.json()) as MeResponse

      if (fetchId === fetchIdRef.current) {
        setMe(data)
      }
    } catch (error: unknown) {
      if (fetchId !== fetchIdRef.current) return

      const normalizedError = error instanceof Error ? error : new Error('Failed to fetch /me')
      console.error('Error fetching /me:', normalizedError)
      setMe(null)
      setError(normalizedError)
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [authHeaders, hasSession, isAuthorized, themeBrand])

  useEffect(() => {
    if (!isAuthorized || !hasSession) {
      autoFetchKeyRef.current = null
      setMe(null)
      setIsLoading(false)
      setError(null)
      return
    }

    if (!themeBrand) {
      autoFetchKeyRef.current = null
      setMe(null)
      setIsLoading(false)
      setError(null)
      return
    }

    const autoFetchKey = `${themeBrand}:${isAuthorized ? '1' : '0'}:${hasSession ? '1' : '0'}`
    if (autoFetchKeyRef.current === autoFetchKey) {
      return
    }

    autoFetchKeyRef.current = autoFetchKey
    void fetchMe()
  }, [fetchMe, hasSession, isAuthorized, themeBrand])

  const value = useMemo<MeContextType>(
    () => ({
      me,
      isLoading,
      error,
      refreshMe: fetchMe,
    }),
    [error, fetchMe, isLoading, me],
  )

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}

export const useMeContext = (): MeContextType => {
  const context = useContext(MeContext)

  if (!context) {
    throw new Error('useMeContext must be used within a MeProvider')
  }

  return context
}
